import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyAuthorProfile, updateMyAuthorProfile } from '../services/authorApi';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './AuthorProfilePage.css';

const AuthorProfilePage = () => {
  const navigate = useNavigate();
  const { authRequired, isAuthenticated, loading: authLoading } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [authorSlug, setAuthorSlug] = useState('');
  const [bio, setBio] = useState('');
  const [suggestedSlug, setSuggestedSlug] = useState('');
  const [initialSlug, setInitialSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!authLoading && authRequired && !isAuthenticated) {
      navigate('/login', { replace: true, state: { from: '/settings/profile' } });
    }
  }, [authLoading, authRequired, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    getMyAuthorProfile()
      .then((profile) => {
        setDisplayName(profile.displayName || profile.principalName || '');
        setAuthorSlug(profile.authorSlug || '');
        setInitialSlug(profile.authorSlug || '');
        setBio(profile.bio || '');
        setSuggestedSlug(profile.suggestedSlug || '');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useDocumentTitle('Author profile');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await updateMyAuthorProfile({
        displayName: displayName.trim(),
        authorSlug: authorSlug.trim() || undefined,
        bio,
      });
      setDisplayName(updated.displayName || '');
      setAuthorSlug(updated.authorSlug || '');
      setInitialSlug(updated.authorSlug || '');
      setBio(updated.bio || '');
      setMessage('Profile saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const slugChanged = initialSlug && authorSlug.trim() && authorSlug.trim() !== initialSlug;

  if (loading) {
    return (
      <div className="author-profile-page">
        <div className="container">
          <p className="author-profile-status">Loading profile…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="author-profile-page">
      <div className="container">
        <header className="author-profile-header">
          <h1>Author profile</h1>
          <p className="author-profile-lead">
            Your public channel at <code>/authors/{authorSlug || 'your-slug'}</code>
          </p>
        </header>

        <form className="author-profile-form" onSubmit={handleSubmit}>
          <label className="form-label" htmlFor="profile-display-name">
            Display name
          </label>
          <input
            id="profile-display-name"
            className="form-control mb-3"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />

          <label className="form-label" htmlFor="profile-slug">
            Channel slug
          </label>
          <input
            id="profile-slug"
            className="form-control mb-1"
            value={authorSlug}
            onChange={(e) => setAuthorSlug(e.target.value.toLowerCase())}
            placeholder={suggestedSlug || 'e.g. jane-analyst'}
          />
          <p className="author-profile-hint mb-3">
            Lowercase letters, numbers, and hyphens (3–63 characters).
            {!authorSlug && suggestedSlug ? ` Suggested: ${suggestedSlug}` : ''}
          </p>
          {slugChanged && (
            <p className="author-profile-warning" role="status">
              Changing your slug will break old channel links. Update bookmarks after saving.
            </p>
          )}

          <label className="form-label" htmlFor="profile-bio">
            Bio
          </label>
          <textarea
            id="profile-bio"
            className="form-control mb-3"
            rows={4}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Optional short bio for your author channel"
          />

          {error && <p className="author-profile-error">{error}</p>}
          {message && <p className="author-profile-success">{message}</p>}

          <div className="author-profile-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save profile'}
            </button>
            {authorSlug && (
              <Link to={`/authors/${authorSlug}`} className="btn btn-outline-primary">
                View channel
              </Link>
            )}
            <Link to="/research" className="btn btn-outline-secondary">
              Back to Research
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthorProfilePage;
